import { useEffect, useState } from "react";
import { TokenPageDataRes } from "src/store/features/api/generated";
import { useAppDispatch } from "src/store/store";
import {
  resetFormField,
  setCompletedFormSection,
  toggleValidForm,
  useTargetState,
} from "src/store/features/TargetOrders.slice";
import { divideNumbers, multiplyNumbers, subtractNumbers } from "../SafeMaths";

export const removeFees = (value: number, fee?: number) => {
  if (!fee) return value;

  // Convert fee percentage to a decimal value
  const feeDecimal = divideNumbers(fee, 100);

  // Calculate the fee amount
  const feeAmount = multiplyNumbers(value, feeDecimal);

  // Subtract the fee amount from the original value
  const valueWithoutFee = subtractNumbers(value, feeAmount);

  return valueWithoutFee;
};

// Validating form inputs for target orders.
const useTargetOrderValidation = (
  orderType: string,
  employee?: boolean,
  currencyValue?: number,
  tokenValue?: number,
  coinMarketPrice?: TokenPageDataRes,
  tokenUnitValue?: number | string,
  tokenField?: boolean,
  tokenFieldTouched?: any,
  currencyField?: boolean,
  currencyFieldIsTouched?: boolean,
  tokenZeroInput?: boolean,
  currencyZeroInput?: boolean,
  fee?: number,
) => {
  const { targetPrice, selectedCurrency, tradeAmount, selectedToken } = useTargetState();
  const maxPriceMultiplier = 1000000000000;
  const minPriceMultiplier = 1 / maxPriceMultiplier;
  const dispatch = useAppDispatch();
  const [validForm, setValidForm] = useState<boolean>(false);
  const { formCompletion } = useTargetState();
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  const [outOfRangeError, setOutOfRangeError] = useState({
    isOutOfRange: false,
    outOfRangeError: "",
  });
  const [minSpend, setMinSpend] = useState(false);
  const [buyTargetInvalid, setBuyTargetInvalid] = useState(false);
  const [sellTargetInValid, setSellTargetInValid] = useState(false);
  const [closeToMarketError, setCloseToMarketError] = useState({
    isClose: false,
    errorMsg: "",
    redirectLink: "/trade",
  });

  /**
   * Validate if the user has enough balance to make the trade
   */

  const validateUserBalance = () => {
    if (orderType === "Buy") {
      if (!selectedCurrency.available) return;
      if (!tradeAmount || !targetPrice) return;
      const orderValue = multiplyNumbers(targetPrice, tradeAmount);

      setInsufficientBalance(orderValue > removeFees(selectedCurrency.available, fee));
    } else if (orderType === "Sell") {
      if (!tokenUnitValue || !selectedToken.available) return;

      setInsufficientBalance(tokenUnitValue > selectedToken.available);
    }
  };

  // Validate Min Spend for user. and need to add employee
  const validateMinimumSpend = () => {
    if (!tradeAmount || !targetPrice) return;
    const tradeValue = targetPrice * tradeAmount;
    const minimum = employee ? 50 : 500;
    if (tradeValue < minimum) {
      setMinSpend(true);
    } else {
      setMinSpend(false);
    }
  };

  /**
   * validateCloseToMarket is a function that checks if the target price is within 5% of the market price for buy or sell order.
   *
   *  orderType - The type of the order, either "Buy" or "Sell"
   *  targetPrice - The target price for the order
   *  coinMarketPrice - An object containing the current market price of the coin
   *  setCloseToMarketError - A function to set the error state for close to market price
   *
   * @returns {void}
   */
  const validateCloseToMarket = () => {
    if (!coinMarketPrice) return;

    if (!tradeAmount || !targetPrice) return;

    const tolerance = orderType === "Buy" ? 0.1 : 1.1;
    const isClose =
      orderType === "Buy"
        ? +coinMarketPrice.price > targetPrice &&
          +coinMarketPrice.price - targetPrice < tolerance * targetPrice
        : +coinMarketPrice.price < targetPrice && targetPrice < +coinMarketPrice.price * tolerance;

    setCloseToMarketError({
      isClose,
      errorMsg: isClose ? "Target within 5% of Market Price" : "",
      redirectLink: "/trade",
    });
  };
  // check the minimum trade value
  // will need to convert to aud
  // Validate if the price is not above current market price
  // Buy only set 90% below the current market price
  const validateOrder = () => {
    const marketPrice = coinMarketPrice ? +coinMarketPrice.price : 0;

    if (!tradeAmount || !targetPrice) return;

    if (orderType === "Buy") {
      if (marketPrice < targetPrice) {
        setBuyTargetInvalid(true);
      } else {
        setBuyTargetInvalid(false);
      }

      if (targetPrice < marketPrice * minPriceMultiplier) {
        setOutOfRangeError({
          isOutOfRange: true,
          outOfRangeError: `Your entered target price is too low, and must be at least ${
            minPriceMultiplier * 100
          }% of the current market price. Please increase your target to continue.`,
        });
      } else {
        setOutOfRangeError({
          isOutOfRange: false,
          outOfRangeError: "",
        });
      }
    } else {
      if (coinMarketPrice) {
        if (+coinMarketPrice.price > targetPrice) {
          setSellTargetInValid(true);
          setOutOfRangeError({
            isOutOfRange: false,
            outOfRangeError: "",
          });
        } else {
          setBuyTargetInvalid(false);
          setSellTargetInValid(false);
          validateUserBalance();

          if (targetPrice > +coinMarketPrice.price * maxPriceMultiplier && orderType === "Sell") {
            setOutOfRangeError({
              isOutOfRange: true,
              outOfRangeError: `Your entered target price is too high, it can be a maximum of ${
                maxPriceMultiplier * 100
              }% the current market price. Please decrease your target to continue.`,
            });
          } else {
            setOutOfRangeError({
              isOutOfRange: false,
              outOfRangeError: "",
            });
          }
        }
      }
    }
  };

  // Validate if the price is not below current market price stop loss not allowed
  // Buy only set 500% above the current market price

  useEffect(() => {
    if (targetPrice && coinMarketPrice) {
      validateOrder();
      validateCloseToMarket();
      validateMinimumSpend();
      validateUserBalance();
    }
  }, [targetPrice, coinMarketPrice]);

  // VALIDATE IF USER CAN AFFORD THE TARGET ORDER
  useEffect(() => {
    if (tradeAmount) {
      validateUserBalance();
      validateMinimumSpend();
      validateCloseToMarket();
    }
  }, [tradeAmount, orderType]);

  useEffect(() => {
    if (orderType) {
      validateUserBalance();
    }
  }, [orderType]);

  useEffect(() => {
    if (
      formCompletion.tokenValueInput &&
      formCompletion.currencyValueInput &&
      formCompletion.tokenInput &&
      formCompletion.currencyInput
    )
      setValidForm(true);
    dispatch(toggleValidForm());
  }, []);

  useEffect(() => {
    if (tokenField && tokenFieldTouched) dispatch(setCompletedFormSection("tokenValueInput"));
  }, [tokenField, tokenFieldTouched]);

  useEffect(() => {
    if (currencyField && currencyFieldIsTouched)
      dispatch(setCompletedFormSection("currencyValueInput"));
  }, [currencyField, currencyFieldIsTouched]);

  useEffect(() => {
    if (!tokenZeroInput && tokenFieldTouched) {
      dispatch(resetFormField("tokenValueInput"));
    }
  }, [tokenZeroInput, tokenFieldTouched]);

  useEffect(() => {
    if (!currencyZeroInput && currencyFieldIsTouched) {
      dispatch(resetFormField("currencyValueInput"));
    }
  }, [currencyZeroInput, currencyFieldIsTouched]);

  return {
    insufficientBalance,
    closeToMarketError,
    outOfRangeError,
    validForm,
    minSpend,
    buyTargetInvalid,
    sellTargetInValid,
  };
};

export default useTargetOrderValidation;
